/**
 * ── A PEÇA CEDE O EIXO A QUEM A CHAMOU ───────────────────────────────────────
 *
 * ## O defeito que este arquivo conserta
 *
 * `className="mb-0"` **não vence** o `mb-5` que o `PageTitle` escreve. As duas
 * são uma classe só — especificidade (0,1,0) contra (0,1,0) — e quando a
 * especificidade empata quem decide é a ORDEM DA FOLHA. Medido no `dist` de
 * 27/08/2026: `.mb-0` sai no byte 12.338 e `.mb-5` no 12.679. O `mb-5` vem
 * depois, o `mb-5` ganha, e o cabeçalho continua custando 20px de banda que
 * alguém já tinha mandado embora.
 *
 * O que faz este defeito sobreviver é que ele **não parece um defeito**:
 *
 * - passa em `tsc` — a prop existe, o tipo é `string`;
 * - passa em revisão — a linha diz exatamente o que quem lê espera que ela faça;
 * - passa em teste de unidade — o DOM tem as duas classes no atributo;
 * - e **funciona metade das vezes**, que é o pior dos mundos. `mb-8` sobre
 *   `mb-5` vence (12.769 > 12.679) porque a escala do Tailwind sai em ordem
 *   crescente na folha. Subir funciona, descer não. Quem testou uma vez subindo
 *   sai convencido de que o controle existe.
 *
 * A varredura de 27/08/2026 sobre `apps/web/src` achou **241 sítios** onde uma
 * classe escrita por quem chamou é jogada fora pelo navegador, em **76
 * arquivos**, e nenhum deles tinha aviso de nada. Os oito culpados são peças
 * compartilhadas, e sete moram em `ui.tsx`.
 *
 * ## As duas saídas que existiam, e por que nenhuma serve
 *
 * **`[&>*]:mb-0` no pai** — o idioma que a `Testeira` já usa, e ele FUNCIONA:
 * gera `.…mb-0 > *`, que é (0,1,1) contra (0,1,0) e ganha por especificidade,
 * não por sorte. Mas ele obriga quem chama a embrulhar a peça numa `<div>` só
 * para pendurar o seletor — e é assim que se perde a `data-forma` colada na
 * peça, e é assim que uma cerca passa a medir o invólucro.
 *
 * **`py-2!`** — o modificador de importância do Tailwind v4. Vence sempre,
 * custa um caractere, e não precisa de nada disto. Mas são 241 sítios: 241
 * `!important` espalhados pelo painel é acessório, e acessório é o que esta
 * obra tira. Além disso ele conserta o sítio e **deixa a armadilha armada** para
 * o próximo.
 *
 * ## O que este arquivo faz no lugar
 *
 * Inverte quem cede. A peça declara os seus padrões por aqui, e `mesclar`
 * **retira o padrão da peça quando quem chamou escreveu no mesmo eixo**. Não é
 * ordem de folha, não é especificidade: a classe perdedora simplesmente não vai
 * para o atributo. O resultado é o que todo mundo já achava que acontecia.
 *
 * ```ts
 * mesclar('w-full px-4 py-3', 'w-20 py-1.5')  // → 'px-4 w-20 py-1.5'
 * ```
 *
 * ## O eixo, e por que a tabela não pode ser confiada de graça
 *
 * Saber que `py-3` e `py-1.5` disputam a mesma propriedade e que `text-[11px]`
 * e `text-dark/75` **não** disputam é conhecimento sobre o Tailwind, e
 * conhecimento sobre biblioteca de terceiro apodrece calado. Uma tabela errada
 * aqui é pior que o defeito original: ela apaga a cor da peça quando quem chamou
 * só quis mudar o tamanho da letra.
 *
 * Por isso a tabela **não é confiada**. A cerca
 * `a-peca-nao-engole-a-classe-do-chamador.test.ts` lê o `dist` construído,
 * descobre quais propriedades CSS cada classe realmente escreve, e cobra
 * `grupoDe` nos dois sentidos:
 *
 * - todo par que a folha diz que colide tem de cair no MESMO grupo;
 * - todo par que cai no mesmo grupo tem de colidir na folha.
 *
 * Um grupo que falta reprova. Um grupo largo demais reprova. A tabela é digitada
 * e **provada contra o artefato**, que é o mais perto de derivada que dá para
 * chegar sem um compilador de Tailwind aqui dentro.
 *
 * `tailwind-merge` resolveria isto e não entra: são kB novos no pacote que já
 * carrega echarts, maplibre, @xyflow, d3 e framer-motion, para um problema que
 * cabe em uma tabela que a cerca confere.
 */

/**
 * As escalas de tamanho de letra do Tailwind.
 *
 * Existe porque `text-` é o prefixo mais ambíguo do vocabulário: `text-sm` é
 * `font-size`, `text-dark` é `color`, e trocar um pelo outro é o erro que apaga
 * a cor da peça. Quem não estiver nesta lista e não for `[<comprimento>]` é cor.
 */
const TAMANHOS_DE_LETRA = new Set([
  'xs', 'sm', 'base', 'lg', 'xl',
  '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl', '9xl',
]);

/** `[11px]`, `[0.7rem]`, `[1.5em]` — valor arbitrário que é COMPRIMENTO. */
const COMPRIMENTO_ARBITRARIO = /^\[[\d.]+(px|rem|em|ch|ex|vw|vh|pt|%)\]$/;

/** O que mais mora sob `text-` e não é nem tamanho nem cor. */
const OUTRO_TEXT: Record<string, string> = {
  left: 'text-align', center: 'text-align', right: 'text-align',
  justify: 'text-align', start: 'text-align', end: 'text-align',
  wrap: 'text-wrap', nowrap: 'text-wrap', balance: 'text-wrap', pretty: 'text-wrap',
  ellipsis: 'text-overflow', clip: 'text-overflow',
};

/**
 * Parte a utilitária em `[variante, resto]`, e o corte ignora colchete.
 *
 * `text-[color:var(--bl-esboco)]` tem dois-pontos DENTRO do valor arbitrário, e
 * um `lastIndexOf(':')` ingênuo lê `text-[color:` como variante e
 * `var(--bl-esboco)]` como utilitária. O resultado é a peça devolver `null` para
 * uma cor e continuar engolindo a do chamador — o defeito de novo, agora dentro
 * do conserto. O link de voltar dos três módulos escuros passa por aqui.
 */
function partirVariante(util: string): [variante: string, resto: string] {
  let profundidade = 0;
  let corte = -1;
  for (let i = 0; i < util.length; i++) {
    const c = util[i];
    if (c === '[' || c === '(') profundidade++;
    else if (c === ']' || c === ')') profundidade--;
    else if (c === ':' && profundidade === 0) corte = i;
  }
  const variante = corte < 0 ? '' : util.slice(0, corte + 1);
  const resto = corte < 0 ? util : util.slice(corte + 1);
  /* O marcador de importância não é eixo, e vem nas duas pontas: `!m-0` na
     forma antiga e `m-0!` na do v4. Ele muda quem ganha, não o que se disputa —
     e é a folha, não esta função, que sabe disso. */
  return [variante, resto.replace(/^!/, '').replace(/!$/, '').replace(/^-/, '')];
}

/**
 * O grupo de conflito de uma utilitária, ou `null` quando ela não disputa eixo
 * nenhum que uma peça desta casa escreva.
 *
 * `null` é a resposta segura: uma classe sem grupo nunca derruba padrão da peça
 * e nunca é derrubada. O custo de um `null` a mais é o defeito original
 * continuar naquele eixo — e a cerca reprova isso, porque ela pergunta à folha.
 *
 * **O que esta função não tem como saber:** ela lê o NOME, e esta casa tem
 * classe escrita à mão cujo nome mente sobre o eixo. `.mb-faixa` e `.mb-bancada`
 * não são margem — são `background` e `border-bottom`, e `.mt-titulo` é
 * `resize`, `width` e a família da fonte. Se uma delas encostar num padrão de
 * peça, `grupoDe` erra. Não há conserto possível daqui, porque o eixo verdadeiro
 * mora no `index.css`; o que há é a cerca conferir, par a par e nos dois
 * sentidos, contra a folha construída — e reprovar antes de o erro chegar à tela.
 */
export function grupoDe(util: string): string | null {
  const [variante, nu] = partirVariante(util);

  const g = (nome: string) => variante + nome;

  /* `text-` primeiro, porque é o prefixo que se parte em quatro grupos. Tratar
     tudo o que não é tamanho como cor faz `text-right` disputar com o
     `text-dark/75` da peça — e o alinhamento apagaria a tinta do rótulo. */
  const letra = nu.match(/^text-(.+)$/);
  if (letra) {
    const valor = letra[1]!;
    if (TAMANHOS_DE_LETRA.has(valor) || COMPRIMENTO_ARBITRARIO.test(valor))
      return g('font-size');
    if (OUTRO_TEXT[valor]) return g(OUTRO_TEXT[valor]!);
    return g('color');
  }

  /* `border-` é o segundo prefixo ambíguo, e o mais caro: `border-dark/12` é
     `border-color` e `border-2` é `border-width`. O cartão selecionado do painel
     morre exatamente aqui — `<Card className="border-accent/40">` desenha a
     borda neutra, e o estado «selecionado» não aparece em cinco telas. */
  if (nu === 'border') return g('border-width');
  const borda = nu.match(/^border-(.+)$/);
  if (borda) {
    /* `border-l-4` é espessura e `border-l-error` é cor: quem decide é o VALOR,
       não o lado. Sem esta distinção `mesclar` deixaria o `border-dark/12` do
       cartão de pé diante de um `border-l-error` — a faixa de erro na lateral
       continuaria cinza. */
    const lado = borda[1]!.match(/^([trblxyse])-(.+)$/);
    const [prefixo, valor] = lado ? [`border-${lado[1]}-`, lado[2]!] : ['border-', borda[1]!];
    const larguraSemValor = lado && /^[trblxyse]$/.test(borda[1]!);
    if (larguraSemValor) return g(`border-${borda[1]}-width`);
    const espessura = /^\d+$/.test(valor) || COMPRIMENTO_ARBITRARIO.test(valor);
    return g(prefixo + (espessura ? 'width' : 'color'));
  }

  /* Tudo o mais é prefixo → propriedade, do mais longo para o mais curto: `min-w`
     tem de casar antes de `w`, senão `min-w-0` vira largura e derruba `w-full`.
     A ordem desta lista é a ordem em que ela é lida. */
  const EIXOS: [prefixo: string, grupo: string][] = [
    ['min-w', 'min-width'], ['max-w', 'max-width'],
    ['min-h', 'min-height'], ['max-h', 'max-height'],
    ['grid-cols', 'grid-template-columns'],
    ['col-span', 'grid-column'],
    ['space-x', 'space-x'], ['space-y', 'space-y'],
    ['gap-x', 'column-gap'], ['gap-y', 'row-gap'],
    ['leading', 'line-height'], ['tracking', 'letter-spacing'],
    ['rounded', 'border-radius'],
    ['bg', 'background-color'],
    ['mt', 'margin-top'], ['mr', 'margin-right'],
    ['mb', 'margin-bottom'], ['ml', 'margin-left'],
    ['mx', 'margin-inline'], ['my', 'margin-block'],
    ['pt', 'padding-top'], ['pr', 'padding-right'],
    ['pb', 'padding-bottom'], ['pl', 'padding-left'],
    ['px', 'padding-inline'], ['py', 'padding-block'],
    ['gap', 'gap'],
    ['w', 'width'], ['h', 'height'],
    ['m', 'margin'], ['p', 'padding'],
  ];
  for (const [prefixo, grupo] of EIXOS) {
    if (nu.startsWith(prefixo + '-')) return g(grupo);
  }
  return null;
}

/**
 * Os grupos que um grupo COBRE.
 *
 * `p-2` de quem chama tem de derrubar o `px-4` E o `py-3` da peça: o atalho
 * escreve as quatro faces, e deixar a face longa da peça de pé devolve o mesmo
 * defeito com outra cara. O contrário não vale — `px-2` de quem chama NÃO
 * derruba o `p-5` da peça, porque `p-5` ainda responde por cima e por baixo, e
 * nesse sentido a folha já resolve sozinha (o atalho sai antes da face longa).
 */
const COBRE: Record<string, string[]> = {
  margin: ['margin-top', 'margin-right', 'margin-bottom', 'margin-left', 'margin-inline', 'margin-block'],
  'margin-inline': ['margin-left', 'margin-right'],
  'margin-block': ['margin-top', 'margin-bottom'],
  padding: ['padding-top', 'padding-right', 'padding-bottom', 'padding-left', 'padding-inline', 'padding-block'],
  'padding-inline': ['padding-left', 'padding-right'],
  'padding-block': ['padding-top', 'padding-bottom'],
  gap: ['row-gap', 'column-gap'],
  'border-width': ['border-t-width', 'border-r-width', 'border-b-width', 'border-l-width'],
  'border-color': ['border-t-color', 'border-r-color', 'border-b-color', 'border-l-color'],
};

/** Os grupos que caem quando quem chama escreve `grupo`. */
export function grupoEOsQueEleCobre(grupo: string): string[] {
  const corte = grupo.lastIndexOf(':');
  const variante = corte < 0 ? '' : grupo.slice(0, corte + 1);
  const nu = corte < 0 ? grupo : grupo.slice(corte + 1);
  return [grupo, ...(COBRE[nu] ?? []).map((x) => variante + x)];
}

/**
 * Os padrões da peça, menos os eixos em que quem chamou já escreveu, mais o que
 * quem chamou escreveu.
 *
 * `padroes` aceita as mesmas formas que o `clsx`, porque metade das peças desta
 * casa escreve padrão condicional (`variant === 'ghost' && 'text-dark/75'`) e
 * obrigar essas a montar a string antes seria trocar um defeito silencioso por
 * uma refatoração de cinquenta linhas.
 */
export function mesclar(
  ...partes: (string | false | null | undefined)[]
): string {
  const className = partes[partes.length - 1];
  const padroes = partes.slice(0, -1);

  const doChamador = typeof className === 'string' ? className.split(/\s+/).filter(Boolean) : [];
  if (doChamador.length === 0)
    return padroes.filter((p): p is string => typeof p === 'string' && p.length > 0).join(' ');

  const cedidos = new Set<string>();
  for (const c of doChamador) {
    const g = grupoDe(c);
    if (g) for (const coberto of grupoEOsQueEleCobre(g)) cedidos.add(coberto);
  }

  const sobrando: string[] = [];
  for (const parte of padroes) {
    if (typeof parte !== 'string' || parte.length === 0) continue;
    for (const u of parte.split(/\s+/)) {
      if (!u) continue;
      const g = grupoDe(u);
      if (g && cedidos.has(g)) continue;
      sobrando.push(u);
    }
  }
  return [...sobrando, ...doChamador].join(' ');
}
